import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  Settings as SettingsIcon,
  Science as TestIcon,
} from '@mui/icons-material';
import { useAuthContext } from '../../contexts/AuthContext';
import { registerServiceWorker, getServiceWorkerRegistration } from '../../utils/serviceWorker';
import {
  isPushSupported,
  requestNotificationPermission,
  getPushSubscription,
  checkSubscriptionStatus,
} from '../../utils/pushNotifications';
import { useVapidKey } from '../../hooks/useVapidKey';
import { mobilePushApi } from '../../services/api/mobile-push.api';
import { pushApi } from '../../services/api/push.api';

interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

interface NotificationSettings {
  events: boolean;
  reminders: boolean;
  social: boolean;
  marketing: boolean;
}

interface PushNotificationManagerProps {
  compact?: boolean;
}

const PushNotificationManager: React.FC<PushNotificationManagerProps> = ({ compact = false }) => {
  const { t } = useTranslation('common');
  const { user } = useAuthContext();
  const { publicKey, loading: keyLoading } = useVapidKey();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [_subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>({
    events: true,
    reminders: true,
    social: false,
    marketing: false,
  });

  const loadSubscriptionState = React.useCallback(async () => {
    try {
      const { isSubscribed: subscribed, subscription: sub } = await checkSubscriptionStatus();
      setIsSubscribed(subscribed);
      if (sub) {
        setSubscription({
          endpoint: sub.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(sub.getKey('p256dh')!),
            auth: arrayBufferToBase64(sub.getKey('auth')!),
          },
        });
      } else {
        setSubscription(null);
      }
    } catch (error) {
      if (process.env['NODE_ENV'] !== 'production') {
        console.error('Ошибка проверки подписки:', error);
      }
      setError(t('notifications.errorCheckState'));
    }
  }, [t]);

  useEffect(() => {
    const init = async () => {
      const supported = isPushSupported();
      setIsSupported(supported);
      if (supported) {
        await registerServiceWorker();
        await loadSubscriptionState();
      }
    };
    init();
  }, [loadSubscriptionState]);

  const subscribeToNotifications = async () => {
    if (!isSupported || !user) return;

    if (!publicKey) {
      setError(t('notifications.errorVapidKey'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Запрашиваем разрешение
      const permission = await requestNotificationPermission();
      if (permission !== 'granted') {
        throw new Error(t('notifications.permissionDenied'));
      }

      // 2. Получаем регистрацию Service Worker
      const registration = await getServiceWorkerRegistration();
      if (!registration) {
        throw new Error(t('notifications.errorServiceWorker'));
      }

      // 3. Получаем подписку от браузера
      const pushSubscription = await getPushSubscription(registration, publicKey);
      if (!pushSubscription) {
        throw new Error(t('notifications.errorSubscription'));
      }

      const subscriptionData = {
        endpoint: pushSubscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(pushSubscription.getKey('p256dh')!),
          auth: arrayBufferToBase64(pushSubscription.getKey('auth')!),
        },
      };

      // 4. Отправляем подписку на backend
      await mobilePushApi.subscribe({
        ...subscriptionData,
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          deviceName: (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform || 'Unknown',
        },
      });
      
      setIsSubscribed(true);
      setSubscription(subscriptionData);

    } catch (error) {
      if (process.env['NODE_ENV'] !== 'production') {
        console.error('Ошибка подписки на уведомления:', error);
      }
      setError(t('notifications.errorSubscribe'));
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromNotifications = async () => {
    if (!isSupported) return;

    setLoading(true);
    setError(null);

    try {
      const registration = await getServiceWorkerRegistration();
      if (!registration) {
        throw new Error('Service Worker не зарегистрирован');
      }

      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Отписываемся в браузере
        await subscription.unsubscribe();
        
        // Отписываемся на backend
        await mobilePushApi.unsubscribe(subscription.endpoint);
      }

      setIsSubscribed(false);
      setSubscription(null);

    } catch (error) {
      if (process.env['NODE_ENV'] !== 'production') {
        console.error('Ошибка отписки от уведомлений:', error);
      }
      setError(t('notifications.errorUnsubscribe'));
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    if (!user || !isSubscribed) return;

    try {
      // Отправляем тестовое уведомление через backend
      await pushApi.sendTest(user.id);
    } catch (error) {
      if (process.env['NODE_ENV'] !== 'production') {
        console.error('Ошибка отправки тестового уведомления:', error);
      }
      setError(t('notifications.errorSendTest'));
    }
  };

  const updateSettings = async (newSettings: NotificationSettings) => {
    if (!user) return;

    try {
      // Настройки сохраняются локально, так как backend API для настроек уведомлений
      // находится в другом модуле (notifications.api.ts)
      setSettings(newSettings);
    } catch (error) {
      if (process.env['NODE_ENV'] !== 'production') {
        console.error('Ошибка обновления настроек:', error);
      }
      setError(t('notifications.errorUpdate'));
    }
  };

  // Утилитарная функция для конвертации ArrayBuffer в Base64
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    const binary = bytes.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
    return btoa(binary);
  };

  if (keyLoading) {
    return (
      <Card>
        <CardContent>
          <Typography>{t('loading')}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!isSupported) {
    return (
      <Card>
        <CardContent>
          <Alert severity="warning">
            {t('notifications.browserNotSupported')}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between" p={1}>
        <Box display="flex" alignItems="center" gap={1}>
          {isSubscribed ? (
            <NotificationsIcon color="primary" fontSize="small" />
          ) : (
            <NotificationsOffIcon color="disabled" fontSize="small" />
          )}
          <Typography variant="body2">
            {t('notifications.pushNotifications')}
          </Typography>
          <Chip
            label={isSubscribed ? t('notifications.on') : t('notifications.off')}
            color={isSubscribed ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Button
          size="small"
          variant={isSubscribed ? 'outlined' : 'contained'}
          color={isSubscribed ? 'error' : 'primary'}
          onClick={isSubscribed ? unsubscribeFromNotifications : subscribeToNotifications}
          disabled={loading}
        >
          {loading ? '...' : isSubscribed ? t('notifications.off') : t('notifications.on')}
        </Button>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" component="h2">
            {t('notifications.pushNotifications')}
          </Typography>
          <Box>
            {isSubscribed && (
              <>
                <Tooltip title={t('notifications.settings')}>
                  <IconButton onClick={() => setSettingsOpen(true)}>
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('notifications.testNotification')}>
                  <IconButton onClick={sendTestNotification}>
                    <TestIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={2} mb={2}>
          {isSubscribed ? (
            <NotificationsIcon color="primary" />
          ) : (
            <NotificationsOffIcon color="disabled" />
          )}
          <Chip
            label={isSubscribed ? t('notifications.enabled') : t('notifications.disabled')}
            color={isSubscribed ? 'success' : 'default'}
            size="small"
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" paragraph>
          {isSubscribed
            ? t('notifications.youWillReceive')
            : t('notifications.enableToNotMiss')
          }
        </Typography>

        <Button
          variant={isSubscribed ? 'outlined' : 'contained'}
          color={isSubscribed ? 'error' : 'primary'}
          onClick={isSubscribed ? unsubscribeFromNotifications : subscribeToNotifications}
          disabled={loading}
          startIcon={isSubscribed ? <NotificationsOffIcon /> : <NotificationsIcon />}
        >
          {loading
            ? t('notifications.processing')
            : isSubscribed
            ? t('notifications.disableNotifications')
            : t('notifications.enableNotifications')
          }
        </Button>

        {/* Диалог настроек */}
        <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
          <DialogTitle>{t('notifications.settings')}</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.events}
                    onChange={(e) => setSettings({ ...settings, events: e.target.checked })}
                  />
                }
                label={t('notifications.eventNotifications')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.reminders}
                    onChange={(e) => setSettings({ ...settings, reminders: e.target.checked })}
                  />
                }
                label={t('notifications.reminders')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.social}
                    onChange={(e) => setSettings({ ...settings, social: e.target.checked })}
                  />
                }
                label={t('notifications.socialNotifications')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.marketing}
                    onChange={(e) => setSettings({ ...settings, marketing: e.target.checked })}
                  />
                }
                label={t('notifications.marketingNotifications')}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSettingsOpen(false)}>
              {t('notifications.cancel')}
            </Button>
            <Button
              onClick={() => {
                updateSettings(settings);
                setSettingsOpen(false);
              }}
              variant="contained"
            >
              {t('notifications.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PushNotificationManager; 